<script setup>
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

defineProps({
  services: { type: Array, default: () => [] },
  selectedServiceId: { type: [String, Number], default: null },
  progress: { type: Object, default: () => ({}) },
  requestedAt: { type: String, default: '' },
  showRequestedAt: { type: Boolean, default: true },
});

const emit = defineEmits(['select', 'work']);
const { t } = useI18n();
const labelFor = status =>
  ({
    draft: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.DRAFT'),
    quoted: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.QUOTED'),
    approved: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.APPROVED'),
    scheduled: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.SCHEDULED'),
    in_progress: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.IN_PROGRESS'),
    completed: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.COMPLETED'),
    accepted: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.ACCEPTED'),
    disputed: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.DISPUTED'),
    canceled: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.CANCELED'),
  })[status] || status;
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat('pt-BR').format(new Date(value))
    : t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
const canWork = service =>
  service.actions?.some(action => ['schedule', 'complete'].includes(action));
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
  >
    <header
      class="flex flex-wrap items-center justify-between gap-3 border-b border-lais-border-soft px-5 py-4"
    >
      <div class="flex flex-wrap items-center gap-2">
        <h2
          class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.SERVICES.TITLE') }}
        </h2>
        <span
          data-test="completed-services"
          class="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700"
        >
          {{
            t('NUCLEO_WORK_ORDERS.DETAIL.SERVICES.COMPLETED', {
              count: progress.completed || 0,
            })
          }}
        </span>
      </div>
      <div
        v-if="showRequestedAt"
        class="flex items-center gap-2 text-xs text-lais-text-muted"
      >
        <span>{{ t('NUCLEO_WORK_ORDERS.DETAIL.SERVICES.REQUESTED_AT') }}</span>
        <span
          data-test="requested-at"
          class="rounded-full bg-lais-bg-soft px-2.5 py-1 font-semibold text-lais-text-body"
        >
          {{ formatDate(requestedAt) }}
        </span>
      </div>
    </header>
    <div class="divide-y divide-lais-border-soft">
      <article
        v-for="service in services"
        :key="service.id"
        class="grid gap-4 p-5 transition-colors sm:grid-cols-[1fr_auto]"
        :class="
          String(service.id) === String(selectedServiceId)
            ? 'bg-lais-primary-50/30'
            : 'bg-white'
        "
      >
        <button
          type="button"
          class="text-left"
          @click="emit('select', service.id)"
        >
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="font-semibold text-lais-text">{{ service.nome }}</h3>
            <span
              v-if="service.tipo_servico?.name"
              class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-body"
            >
              {{ service.tipo_servico.name }}
            </span>
            <span
              v-if="service.servico_detalhado?.name"
              class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-xs font-semibold text-lais-primary"
            >
              {{ service.servico_detalhado.name }}
            </span>
            <span
              class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-body"
            >
              {{ labelFor(service.status) }}
            </span>
          </div>
          <p v-if="service.descricao" class="mt-2 text-sm text-lais-text-body">
            {{ service.descricao }}
          </p>
          <p
            v-if="service.valor_servico_detalhado"
            class="mt-2 text-sm text-lais-text-body"
          >
            {{
              t('NUCLEO_WORK_ORDERS.DETAIL.SERVICES.CONTEXT_VALUE', {
                value: service.valor_servico_detalhado,
              })
            }}
          </p>
          <p class="mt-2 text-xs text-lais-text-muted">
            {{
              t('NUCLEO_WORK_ORDERS.DETAIL.SERVICES.META', {
                dueAt: formatDate(service.data_previsao),
                count: service.evidencia_count,
              })
            }}
          </p>
        </button>
        <div v-if="canWork(service)" class="flex flex-wrap items-start gap-2">
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            @click="emit('work', service)"
          >
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.WORK') }}
          </LaisPrimaryButton>
        </div>
      </article>
    </div>
  </section>
</template>
